import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Fuzzy Search Manager - handles typo-tolerant search with Fuse.js
 */
class FuzzySearchManager {
  /** @type {any} Fuse.js instance */
  #fuse = null;

  /** @type {Array} Product index */
  #products = [];

  /** @type {boolean} Whether the index is loaded */
  #indexLoaded = false;

  /** @type {boolean} Whether we're currently loading */
  #loading = false;

  /** @type {Promise|null} Loading promise */
  #loadingPromise = null;

  /**
   * Initialize Fuse.js with product data
   */
  async initialize() {
    if (this.#indexLoaded || this.#loading) {
      return this.#loadingPromise;
    }

    this.#loading = true;
    this.#loadingPromise = this.#loadIndex();

    try {
      await this.#loadingPromise;
    } finally {
      this.#loading = false;
    }

    return this.#loadingPromise;
  }

  async #loadIndex() {
    try {
      // Load Fuse.js if not already loaded
      if (!window.Fuse) {
        await this.#loadFuseJS();
      }

      // Fetch product index section
      const response = await fetch('/?section_id=search-product-index');
      if (!response.ok) throw new Error('Failed to fetch product index');

      const html = await response.text();

      // Parse the JSON from the script tag
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const scriptTag = doc.getElementById('search-product-index-data');

      if (!scriptTag) throw new Error('Product index script tag not found');

      const data = JSON.parse(scriptTag.textContent);
      this.#products = data.products || [];

      // Initialize Fuse.js with fuzzy search options
      this.#fuse = new window.Fuse(this.#products, {
        keys: [
          { name: 'title', weight: 0.4 },
          { name: 'vendor', weight: 0.2 },
          { name: 'type', weight: 0.15 },
          { name: 'tags', weight: 0.15 },
          { name: 'description_snippet', weight: 0.1 }
        ],
        threshold: 0.4, // 0 = exact match, 1 = match anything
        distance: 100, // How far to search for a match
        minMatchCharLength: 2,
        includeScore: true,
        includeMatches: true,
        ignoreLocation: true,
        useExtendedSearch: true,
        findAllMatches: true
      });

      this.#indexLoaded = true;
      console.log(`FuzzySearch: Indexed ${this.#products.length} products`);

    } catch (error) {
      console.error('FuzzySearch: Failed to initialize', error);
      throw error;
    }
  }

  async #loadFuseJS() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = Theme.routes.root + 'cdn/shop/t/5/assets/fuse.min.js';
      script.onload = resolve;
      script.onerror = () => {
        // Fallback to CDN
        const cdnScript = document.createElement('script');
        cdnScript.src = 'https://cdn.jsdelivr.net/npm/fuse.js@7.0.0/dist/fuse.min.js';
        cdnScript.onload = resolve;
        cdnScript.onerror = reject;
        document.head.appendChild(cdnScript);
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Search products with fuzzy matching
   * @param {string} query - Search query
   * @param {number} limit - Maximum results
   * @returns {Array} Sorted results by relevance
   */
  search(query, limit = 8) {
    if (!this.#fuse || !query.trim()) {
      return [];
    }

    const results = this.#fuse.search(query, { limit });

    // Sort by score (lower is better match) and return items
    return results
      .sort((a, b) => a.score - b.score)
      .map(result => ({
        ...result.item,
        score: result.score,
        matches: result.matches
      }));
  }

  /**
   * Check if index is ready
   */
  get isReady() {
    return this.#indexLoaded;
  }
}

// Global fuzzy search instance
const fuzzySearch = new FuzzySearchManager();

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Check if Algolia autocomplete is active on this search input
   * @returns {boolean}
   */
  #isAlgoliaActive() {
    if (!this.refs.searchInput) return false;

    // Check multiple ways Algolia might indicate it's active:
    // 1. Algolia container wrapper
    const algoliaContainer = this.refs.searchInput.closest('.aa-Autocomplete');
    // 2. Algolia main container in the component
    const algoliaMainContainer = this.querySelector('.aa-MainContainer');
    // 3. Algolia global object
    const hasAlgoliaGlobal = !!(window.aa || window.algoliasearch);
    // 4. Check if input has Algolia data attributes
    const hasAlgoliaAttrs = this.refs.searchInput.hasAttribute('data-autocomplete') ||
                           this.refs.searchInput.closest('[data-autocomplete]');
    // 5. Check for Algolia elements in the document
    const hasAlgoliaElements = !!(document.querySelector('.aa-Autocomplete') ||
                                  document.querySelector('[data-autocomplete]') ||
                                  document.querySelector('.aa-DetachedSearchButton'));

    return !!(algoliaContainer || algoliaMainContainer || hasAlgoliaGlobal || hasAlgoliaAttrs || hasAlgoliaElements);
  }

  /**
   * Setup integration with Algolia when it's detected
   * This helps trigger Algolia's UI when our search modal opens
   */
  #setupAlgoliaIntegration() {
    const { dialog } = this;
    const { signal } = this.#controller;

    if (!dialog) return;

    // When dialog opens, try to trigger Algolia
    dialog.addEventListener(DialogOpenEvent.eventName, () => {
      this.#triggerAlgoliaSearch();
    }, { signal });

    // Forward input events to Algolia if it has its own input
    if (this.refs.searchInput) {
      this.refs.searchInput.addEventListener('input', () => {
        this.#syncToAlgolia(this.refs.searchInput.value);
      }, { signal });

      // Also trigger on focus
      this.refs.searchInput.addEventListener('focus', () => {
        this.#triggerAlgoliaSearch();
      }, { signal });
    }
  }

  /**
   * Try to trigger Algolia's search UI
   */
  #triggerAlgoliaSearch() {
    // Try to find and click Algolia's detached search button
    const algoliaButton = document.querySelector('.aa-DetachedSearchButton');
    if (algoliaButton instanceof HTMLElement) {
      algoliaButton.click();
      return;
    }

    // Try to focus Algolia's input
    const algoliaInput = document.querySelector('.aa-Input');
    if (algoliaInput instanceof HTMLInputElement) {
      algoliaInput.focus();
      return;
    }

    // Try to trigger via Algolia's global API if available
    if (window.aa && typeof window.aa === 'function') {
      try {
        // Algolia Insights API
        console.log('Algolia API detected');
      } catch (e) {
        console.warn('Failed to trigger Algolia:', e);
      }
    }
  }

  /**
   * Sync our search input value to Algolia's input
   * @param {string} value - The search value
   */
  #syncToAlgolia(value) {
    const algoliaInput = document.querySelector('.aa-Input');
    if (algoliaInput instanceof HTMLInputElement && algoliaInput !== this.refs.searchInput) {
      algoliaInput.value = value;
      // Dispatch input event to trigger Algolia's search
      algoliaInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput) {
      if (this.refs.searchInput.value.length > 0) {
        this.#showResetButton();
      }

      // Handle immediate UI updates (reset button visibility)
      this.refs.searchInput.addEventListener('input', this.#handleSearchInput, { signal });
    }

    // Always enable native search as primary or fallback
    // Algolia (if present) will render its own UI separately
    if (this.refs.searchInput) {
      this.refs.searchInput.addEventListener('input', this.search, { signal });
    }
    if (this.refs.predictiveSearchResults) {
      this.refs.predictiveSearchResults.style.display = '';
    }

    // Check for Algolia after a short delay to allow it to initialize
    setTimeout(() => {
      if (this.#isAlgoliaActive()) {
        console.log('Algolia detected - native search will work alongside Algolia');
        this.#setupAlgoliaIntegration();
      } else {
        console.log('Algolia not detected - using native Shopify search only');
      }
    }, 500);

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Direct input handler for instant search
   * @param {InputEvent} event - The input event
   */
  #handleSearchInput = (event) => {
    // Immediately show/hide reset button based on input value
    const searchTerm = this.refs.searchInput.value.trim();
    if (searchTerm.length > 0) {
      this.#showResetButton();
    } else {
      this.#hideResetButton();
    }
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Allow all clicks on interactive elements to proceed normally
    // Including Algolia autocomplete elements (aa-* classes)
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('.predictive-search-results__card') ||
      target.closest('[href]') ||
      target.closest('[role="button"]') ||
      target.closest('[role="option"]') ||
      target.closest('[role="listbox"]') ||
      // Algolia autocomplete elements
      target.closest('.aa-Item') ||
      target.closest('.aa-ItemLink') ||
      target.closest('.aa-Panel') ||
      target.closest('.aa-List') ||
      target.closest('.aa-Source') ||
      target.closest('[class*="aa-"]');

    // Only focus input if clicking on non-interactive area
    if (!isInteractiveElement && this.refs.searchInput) {
      event.preventDefault();
      this.refs.searchInput.focus();
    }
    // For interactive elements, let the click proceed normally - don't prevent default or stop propagation
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   * Using a shorter debounce time (50ms) for more instant search experience.
   */
  search = debounce(async (event) => {
    // Get the current search term from the input
    const searchTerm = this.refs.searchInput?.value?.trim() || '';
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    // Show reset button immediately for better UX
    this.#showResetButton();

    // Try fuzzy search first for typo tolerance
    try {
      // Initialize fuzzy search if not ready (loads in background)
      if (!fuzzySearch.isReady) {
        fuzzySearch.initialize().catch(console.error);
      }

      // If fuzzy search is ready, use it for instant results
      if (fuzzySearch.isReady) {
        const fuzzyResults = fuzzySearch.search(searchTerm, 8);
        if (fuzzyResults.length > 0) {
          this.#renderFuzzyResults(fuzzyResults, searchTerm);
          return;
        }
      }
    } catch (error) {
      console.warn('Fuzzy search failed, falling back to server search:', error);
    }

    // Fall back to server-side search
    this.#getSearchResults(searchTerm);
  }, 50);

  /**
   * Render fuzzy search results client-side
   * @param {Array} results - Fuzzy search results sorted by relevance
   * @param {string} searchTerm - The search term
   */
  #renderFuzzyResults(results, searchTerm) {
    const { predictiveSearchResults } = this.refs;
    if (!predictiveSearchResults) return;

    const productsTitle = Theme.translations?.search_results_resource_products || 'מוצרים';
    const viewAllText = Theme.translations?.search_results_view_all || 'צפה בכל התוצאות';

    const resultsHTML = `
      <div
        id="predictive-search-results"
        class="predictive-search-dropdown"
        role="listbox"
        aria-label="תוצאות חיפוש"
        aria-expanded="true"
        data-transition-container
      >
        <div class="predictive-search-results__inner" data-search-results>
          <div class="visually-hidden" role="status" aria-live="polite">
            נמצאו ${results.length} תוצאות עבור "${searchTerm}"
          </div>
          <div id="predictive-search-products" class="predictive-search-results__products">
            <h4 class="predictive-search-results__title">${productsTitle}</h4>
            <ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled" role="listbox" aria-label="${productsTitle}">
              ${results.map((product, index) => this.#renderProductCard(product, index)).join('')}
            </ul>
          </div>
        </div>
      </div>
    `;

    // Parse and morph the results
    const parser = new DOMParser();
    const doc = parser.parseFromString(resultsHTML, 'text/html');
    const resultsContainer = doc.getElementById('predictive-search-results');

    if (resultsContainer) {
      morph(predictiveSearchResults, resultsContainer);

      if (this.refs.searchInput) {
        this.refs.searchInput.setAttribute('aria-expanded', 'true');
      }

      this.#resetScrollPositions();
    }
  }

  /**
   * Render a single product card for fuzzy results
   * @param {Object} product - Product data
   * @param {number} index - Result index
   * @returns {string} HTML string
   */
  #renderProductCard(product, index) {
    const scorePercent = Math.round((1 - (product.score || 0)) * 100);

    return `
      <li class="predictive-search-results__card predictive-search-results__card--product"
          ref="resultsItems[]"
          data-search-result-index="search-results-${index}"
          data-relevance-score="${scorePercent}">
        <product-card-link data-product-id="${product.id}">
          <div class="resource-card" data-resource-type="product">
            <a class="resource-card__link" href="${product.url}">
              <span class="visually-hidden">${this.#escapeHtml(product.title)}</span>
            </a>
            <div class="resource-card__media" style="--resource-card-aspect-ratio: 4 / 5;">
              ${product.image?.src ? `
                <img
                  src="${product.image.src}"
                  alt="${this.#escapeHtml(product.image.alt || product.title)}"
                  loading="lazy"
                  class="resource-card__image"
                  width="200"
                  height="250"
                >
              ` : ''}
            </div>
            <div class="resource-card__content">
              <p class="resource-card__title paragraph">${this.#escapeHtml(product.title)}</p>
              ${product.vendor ? `<p class="resource-card__vendor">${this.#escapeHtml(product.vendor)}</p>` : ''}
              <p class="resource-card__price">${product.price}</p>
            </div>
          </div>
        </product-card-link>
      </li>
    `;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  #escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) {
      console.warn('PredictiveSearchComponent: sectionId not found');
      return;
    }

    if (!this.refs.predictiveSearchResults) {
      console.warn('PredictiveSearchComponent: predictiveSearchResults ref not found');
      return;
    }

    // Use regular search URL since predictive search may be disabled
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('type', 'product,collection,page,article');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      
      if (abortController.signal.aborted) return;

      if (!resultsMarkup) {
        console.warn('PredictiveSearchComponent: No results markup returned');
        return;
      }

      // Parse the HTML to extract the actual results container
      // The section renderer returns HTML wrapped in a shopify-section div
      const parser = new DOMParser();
      const doc = parser.parseFromString(resultsMarkup, 'text/html');
      
      // The section HTML is wrapped in a div with class "shopify-section"
      // We need to find the actual predictive-search-results container inside it
      let resultsContainer = doc.getElementById('predictive-search-results');
      
      if (!resultsContainer) {
        // Try to find it inside a shopify-section wrapper
        const sectionWrapper = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
        if (sectionWrapper) {
          resultsContainer = sectionWrapper.querySelector('#predictive-search-results') ||
                           sectionWrapper.querySelector('.predictive-search-dropdown');
        }
      }
      
      if (!resultsContainer) {
        // Last resort: try to find by class anywhere
        resultsContainer = doc.querySelector('.predictive-search-dropdown');
      }
      
      if (!resultsContainer) {
        console.error('PredictiveSearchComponent: Results container not found in markup');
        console.log('Full markup received:', resultsMarkup);
        return;
      }

      // Morph the results into the content container
      // This replaces whatever is currently in predictiveSearchResults (e.g., empty state)
      morph(predictiveSearchResults, resultsContainer);
      
      // Update aria-expanded on the input to indicate results are shown
      if (this.refs.searchInput) {
        this.refs.searchInput.setAttribute('aria-expanded', 'true');
      }
      
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error('PredictiveSearchComponent: Error fetching search results:', error);
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
